import { Controller } from "@hotwired/stimulus"

// USAGE: Auto-opens Bootstrap modals based on URL hash fragments
//
// 1. Add controller to a container element:
//    <div data-controller="auto-open-modal"
//         data-auto-open-modal-prefix-value="open-comments"
//         data-auto-open-modal-modal-prefix-value="commentsModal">
//
// 2. Have Bootstrap modals with IDs following the pattern:
//    <div class="modal" id="commentsModal123">...</div>
//    <div class="modal" id="commentsModal456">...</div>
//
// 3. Create notification/email links with hash fragments:
//    Rails: admin_reports_path(anchor: "open-comments-123")
//    URL:  /admin/reports#open-comments-123
//
// 4. When user visits URL, modal "commentsModal123" opens automatically
//
// CONFIGURATION:
// - prefix-value: What to look for in URL hash (e.g. "open-comments")
// - modal-prefix-value: Start of modal IDs (e.g. "commentsModal")
//
// EXAMPLE:
// URL: /page#open-comments-user-456
// Opens modal with ID: commentsModaluser-456

export default class extends Controller {
  static values = { 
    prefix: String,     // e.g., "open-comments"
    modalPrefix: String // e.g., "commentsModal"
  }

  connect() {
    this.checkForHashModal()
    
    // Listen for hash changes (in case user navigates with browser back/forward)
    this.boundHashHandler = this.checkForHashModal.bind(this)
    window.addEventListener('hashchange', this.boundHashHandler)
  }

  disconnect() {
    window.removeEventListener('hashchange', this.boundHashHandler)
  }

  checkForHashModal() {
    const hash = window.location.hash

    if (!hash || !this.prefixValue) return
    
    const hashPrefix = `#${this.prefixValue}-`
    
    if (hash.startsWith(hashPrefix)) {
      const identifier = hash.replace(hashPrefix, '')
      this.openModalForIdentifier(identifier)
    }
  }

  openModalForIdentifier(identifier) {
    const modalId = `${this.modalPrefixValue}${identifier}`
    const modalElement = document.getElementById(modalId)
    
    if (modalElement) {
      // Use Bootstrap's modal API
      const modal = new bootstrap.Modal(modalElement)
      modal.show()
      
      // Clean up the hash so it doesn't interfere with navigation
      this.clearHash()
    } else {
      // Modal not found - might be on wrong page or not loaded yet
      console.warn(`Modal not found: ${modalId}`)
      
      // Optionally retry after a short delay (useful for dynamically loaded content)
      setTimeout(() => {
        const retryModal = document.getElementById(modalId)
        if (retryModal) {
          const modal = new bootstrap.Modal(retryModal)
          modal.show()
          this.clearHash()
        }
      }, 500)
    }
  }

  clearHash() {
    // Remove hash without triggering hashchange event or affecting browser history
    if (history.replaceState) {
      history.replaceState(null, null, window.location.pathname + window.location.search)
    }
  }
}
